// src/components/pipelines/nodes/KanbanNode.jsx - Full Interactive Kanban Board Node
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useViewport } from '@xyflow/react';
import { Card, CardContent, CardHeader } from '../../ui/card.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { Plus, X, GripVertical, MoreVertical, Settings, Edit2, Check } from 'lucide-react';
import { Switch } from '../../ui/switch.tsx';
import { usePipelines } from '../../../hooks/usePipelines';
import InlineStageConfig from './InlineStageConfig';

/**
 * CustomerCard - Individual customer card within a stage
 */
const CustomerCard = ({ customer }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: customer.id,
    data: {
      type: 'customer',
      customer,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-slate-800/50 border border-slate-700/50 rounded-md p-2 mb-2 cursor-grab active:cursor-grabbing hover:bg-slate-700/50 transition-colors group"
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-3 h-3 text-slate-500 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-200 truncate">
            {customer.name}
          </div>
          {customer.email && (
            <div className="text-xs text-slate-500 truncate">
              {customer.email}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * StageColumn - A single stage/column of the kanban board
 */
const StageColumn = ({ stage, customers, onRemove, onRename, onConfigureClick, isConfigExpanded, canRemove }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(stage.name);

  // Make column a droppable zone
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: {
      type: 'stage',
      stage,
    },
  });

  const handleNameSave = () => {
    if (editedName.trim()) {
      onRename(stage.id, editedName);
      setIsEditingName(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-64 bg-slate-900/50 rounded-lg border transition-all ${
        isConfigExpanded
          ? 'border-purple-500 border-2 bg-purple-500/10 shadow-lg shadow-purple-500/20'
          : isOver
          ? 'border-purple-500 bg-purple-500/10'
          : 'border-slate-700/50'
      }`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSave();
                if (e.key === 'Escape') {
                  setEditedName(stage.name);
                  setIsEditingName(false);
                }
              }}
              autoFocus
              className="h-6 text-xs bg-slate-800 border-slate-700 text-slate-100"
            />
          ) : (
            <div
              className="flex-1 text-sm font-semibold text-slate-200 cursor-pointer hover:text-purple-300 transition-colors"
              onClick={() => setIsEditingName(true)}
            >
              {stage.name}
            </div>
          )}

          <div className="flex items-center gap-1">
            <Badge variant="secondary" className="text-xs">
              {customers.length}
            </Badge>
            {/* Stage rule count badge */}
            {stage.ruleCount > 0 && (
              <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-400">
                {stage.ruleCount}
              </Badge>
            )}
            {/* Configure button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onConfigureClick(stage);
              }}
              className={`h-5 w-5 p-0 transition-all ${
                isConfigExpanded
                  ? 'text-purple-400 bg-purple-500/20'
                  : 'text-slate-500 hover:text-blue-400'
              }`}
              title="Configure automation"
            >
              <Settings className={`w-3 h-3 ${isConfigExpanded ? 'animate-pulse' : ''}`} />
            </Button>
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(stage.id)}
                className="h-5 w-5 p-0 text-slate-500 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Column Content - Customer Cards */}
      <div className="p-2 max-h-64 overflow-y-auto">
        <SortableContext
          items={customers.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {customers.map((customer) => (
            <CustomerCard key={customer.id} customer={customer} />
          ))}
        </SortableContext>

        {customers.length === 0 && (
          <div className="text-xs text-slate-500 italic text-center py-4">
            No customers
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * KanbanNode - Full interactive kanban board as a React Flow node
 *
 * Node data structure:
 * {
 *   pipelineId: 'uuid',
 *   name: 'Onboarding Pipeline',
 *   stages: [{id, name, order}, ...],
 *   customers: [{id, name, email, stageId}, ...],
 *   isActive: true
 * }
 */
const KanbanNode = ({ data, selected }) => {
  const { pipelineId } = data;

  const [activeCustomer, setActiveCustomer] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [isAddingStage, setIsAddingStage] = useState(false);
  const [expandedStageId, setExpandedStageId] = useState(null); // ID of stage being configured (inline)
  const [isEditingBoardName, setIsEditingBoardName] = useState(false);
  const [editedBoardName, setEditedBoardName] = useState('');

  // Get React Flow viewport for zoom scaling
  const { zoom } = useViewport();

  // Get pipeline data from store (single source of truth)
  const {
    pipelines,
    updatePipeline,
    createStage,
    updateStage,
    deleteStage,
    moveCustomerToStage,
    loadPipelineBoard,
  } = usePipelines();

  // Find this pipeline in the store
  const pipeline = pipelines.find(p => p.id === pipelineId);
  const name = pipeline?.name || data.name || 'Kanban Board';
  const isActive = pipeline?.is_active ?? data.isActive ?? true;

  // Get stages from pipeline data (stages live in the pipeline, not as separate entity)
  // For now, use the stages from data until we load full pipeline details
  const stages = data.stages || [];
  const customers = data.customers || [];

  // Configure drag sensors (more sensitive than React Flow)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    })
  );

  // Configure measuring to fix drag overlay positioning
  const measuring = {
    droppable: {
      strategy: 'always',
    },
  };

  // Get customers for a specific stage
  const getCustomersForStage = (stageId) => {
    return customers.filter(c => c.stageId === stageId);
  };

  // Handle customer drag start
  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'customer') {
      setActiveCustomer(active.data.current.customer);
    }
  };

  // Handle customer drag over (move between stages)
  // Note: We don't do optimistic updates here - just wait for backend response
  const handleDragOver = (event) => {
    // Visual feedback is handled by DnD Kit's built-in drag overlay
    // No local state updates needed
  };

  // Handle customer drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Handle drop on stage column
    if (over && active.data.current?.type === 'customer' && over.data.current?.type === 'stage') {
      const activeCustomer = active.data.current.customer;
      const overStage = over.data.current.stage;

      if (activeCustomer.stageId !== overStage.id) {
        // Persist to backend (no optimistic update - wait for socket response)
        if (pipelineId && activeCustomer.id && overStage.id) {
          moveCustomerToStage(activeCustomer.id, overStage.id);
        }
      }
    }

    setActiveCustomer(null);
  };

  // Add new stage
  const handleAddStage = () => {
    if (newStageName.trim() && pipelineId) {
      // Call store method - no optimistic update, wait for socket response
      createStage(pipelineId, {
        name: newStageName,
        order: stages.length,
      });

      // Clear form
      setNewStageName('');
      setIsAddingStage(false);
    }
  };

  // Remove stage
  const handleRemoveStage = (stageId) => {
    if (stages.length <= 1) {
      alert('Cannot remove the last stage');
      return;
    }

    const hasCustomers = customers.some(c => c.stageId === stageId);
    if (hasCustomers) {
      alert('Cannot remove stage with customers. Move customers first.');
      return;
    }

    // Call store method - no optimistic update, wait for socket response
    deleteStage(stageId);
  };

  // Rename stage
  const handleRenameStage = (stageId, newName) => {
    // Call store method - no optimistic update, wait for socket response
    updateStage(stageId, { name: newName });
  };

  // Start editing board name
  const handleStartEditBoardName = () => {
    setEditedBoardName(name);
    setIsEditingBoardName(true);
  };

  // Save board name
  const handleSaveBoardName = () => {
    if (editedBoardName.trim() && editedBoardName !== name) {
      updatePipeline(pipelineId, { name: editedBoardName.trim() });
    }
    setIsEditingBoardName(false);
  };

  // Cancel editing board name
  const handleCancelEditBoardName = () => {
    setEditedBoardName(name);
    setIsEditingBoardName(false);
  };

  // Toggle active status
  const handleToggleActive = (checked) => {
    updatePipeline(pipelineId, { is_active: checked });
  };

  const totalCustomers = customers.length;

  return (
    <div className="kanban-node">
      {/* Input handle (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-purple-500"
      />

      <Card className={`
        min-w-[800px] max-w-[1200px]
        ${selected ? 'ring-2 ring-purple-500 shadow-lg' : 'shadow-md'}
        ${!isActive ? 'opacity-60' : ''}
        transition-all duration-200
        bg-gradient-to-br from-purple-500/10 to-purple-600/5
        border-purple-500/20
      `}>
        {/* Drag Handle Header - Only this area moves the node */}
        <CardHeader className="pb-3 cursor-move border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GripVertical className="w-4 h-4 text-slate-500" />

              {/* Editable Board Name */}
              <div className="flex items-center gap-2">
                {isEditingBoardName ? (
                  <div className="flex items-center gap-2 no-drag">
                    <span className="text-lg">📋</span>
                    <Input
                      value={editedBoardName}
                      onChange={(e) => setEditedBoardName(e.target.value)}
                      onBlur={handleSaveBoardName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveBoardName();
                        if (e.key === 'Escape') handleCancelEditBoardName();
                      }}
                      autoFocus
                      className="h-8 text-base font-semibold bg-slate-800 border-slate-700 text-purple-100 w-64"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleSaveBoardName}
                      className="h-6 w-6 p-0 text-green-400 hover:text-green-300"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-semibold text-purple-100 flex items-center gap-2">
                      📋 {name}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditBoardName();
                      }}
                      className="h-6 w-6 p-0 text-slate-500 hover:text-purple-400 no-drag"
                      title="Edit board name"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Active/Inactive Toggle */}
              <div className="flex items-center gap-2 no-drag">
                <Switch
                  checked={isActive}
                  onCheckedChange={handleToggleActive}
                  className="data-[state=checked]:bg-green-600"
                />
                <span className="text-xs text-slate-400">
                  {isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Customer Count */}
            <div className="text-xs text-slate-400">
              <span className="font-semibold text-purple-300">{totalCustomers}</span> customers
            </div>
          </div>
        </CardHeader>

        {/* Kanban Board Content - NO DRAG (won't move node) */}
        <CardContent className="p-4 no-drag">
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            measuring={measuring}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-3 overflow-x-auto pb-2">
              {stages.map((stage) => (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  customers={getCustomersForStage(stage.id)}
                  onRemove={handleRemoveStage}
                  onRename={handleRenameStage}
                  onConfigureClick={(stage) => {
                    // Toggle: if clicking same stage, close; otherwise open new one
                    setExpandedStageId(expandedStageId === stage.id ? null : stage.id);
                  }}
                  isConfigExpanded={stage.id === expandedStageId}
                  canRemove={stages.length > 1}
                />
              ))}

              {/* Add Stage Column */}
              <div className="flex-shrink-0 w-64">
                {isAddingStage ? (
                  <div className="bg-slate-900/30 rounded-lg border-2 border-dashed border-slate-700/50 p-3">
                    <Input
                      placeholder="Stage name..."
                      value={newStageName}
                      onChange={(e) => setNewStageName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddStage();
                        if (e.key === 'Escape') {
                          setIsAddingStage(false);
                          setNewStageName('');
                        }
                      }}
                      autoFocus
                      className="mb-2 bg-slate-800 border-slate-700 text-slate-100 text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleAddStage}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 h-7 text-xs"
                      >
                        Add
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsAddingStage(false);
                          setNewStageName('');
                        }}
                        className="flex-1 h-7 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingStage(true)}
                    className="w-full h-32 border-2 border-dashed border-slate-700/50 hover:border-purple-500/50 hover:bg-slate-800/50 text-slate-400 hover:text-purple-300"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Stage
                  </Button>
                )}
              </div>
            </div>

            {/* Drag Overlay - Shows dragged customer */}
            {typeof window !== 'undefined' && createPortal(
              <DragOverlay
                dropAnimation={null}
                modifiers={[snapCenterToCursor]}
              >
                {activeCustomer && (
                  <div
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: '0 0',
                    }}
                  >
                    <Card className="w-64 bg-slate-700 border-2 border-purple-500 shadow-2xl opacity-95">
                      <CardContent className="p-2">
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-3 h-3 text-slate-500 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-200 truncate">
                              {activeCustomer.name}
                            </div>
                            {activeCustomer.email && (
                              <div className="text-xs text-slate-400 truncate">
                                {activeCustomer.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        </CardContent>

        {/* Inline Stage Configuration (expands below stages) */}
        {expandedStageId && (
          <div className="px-4 pb-4">
            <InlineStageConfig
              stage={stages.find(s => s.id === expandedStageId)}
              onClose={() => setExpandedStageId(null)}
            />
          </div>
        )}
      </Card>

      {/* Output handle (right side) */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-purple-500"
      />
    </div>
  );
};

export default KanbanNode;
